import { useTextesBons } from '../features/bons/textes'
import { SelecteurLangue } from './SelecteurLangue'
import { useTextePublic } from '../lib/textes-publics'
/**
 * Coque du portail abonné (`/portail/*`) — POR-01 à POR-05.
 *
 * Public, sobre, en lecture seule. Le pied de page rappelle la règle sans
 * détour : toute correction passe par le théâtre (POR-05).
 */

import { Suspense } from 'react'
import { Link, Outlet, useNavigate } from 'react-router'

import { useAbonne, useAuth } from '../auth'
import { estEvenement, titreEspaceAbonne, useTypeTenant } from '../lib/vocabulaire'
import { CHEMINS } from '../lib/chemins'
import { useTheatre } from '../lib/theatre'
import { Button } from '../ui/Button'
import { ChargementPage } from '../ui/Spinner'
import { BandeauEcheance, EcranTenantBloquant, useEtatTenantBloquant } from './EtatTenant'

export function LayoutPortail() {
  const texteInterface = useTextePublic()
  const textesBons = useTextesBons()

  const abonne = useAbonne()
  /* ★ EVT-04 — chez un organisateur d'événement ponctuel il n'y a ni abonné ni
     formule : l'espace est celui d'une PERSONNE, et l'on n'y change pas de
     formule (`lib/vocabulaire.ts`). */
  const typeTenant = useTypeTenant()
  const { deconnecter } = useAuth()
  const naviguer = useNavigate()
  const etatTenant = useEtatTenantBloquant()
  /* Même requête publique que `useEtatTenantBloquant()` ci-dessus : TanStack
     Query les déduplique, l'en-tête ne coûte donc aucun appel de plus. */
  const { nom: nomTheatre } = useTheatre()

  async function seDeconnecter(): Promise<void> {
    await deconnecter()
    await naviguer(CHEMINS.connexionAbonne, { replace: true })
  }

  // LOT F3 — tenant expiré/suspendu : voir EtatTenant.tsx pour la justification.
  if (etatTenant !== undefined) return <EcranTenantBloquant etat={etatTenant} />

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <BandeauEcheance />

      <header className="border-b border-slate-200">
        <div className="mx-auto flex w-full max-w-4xl items-center gap-3 px-4 py-4">
          <Link to={CHEMINS.portail} className="min-w-0">
            <p className="truncate text-sm font-semibold tracking-wide text-rideau-800 uppercase">
              {nomTheatre}
            </p>
            <p className="text-xs text-slate-500">
              {titreEspaceAbonne(typeTenant, texteInterface.langue)}
            </p>
          </Link>

          <div className="ml-auto flex items-center gap-3">
            <SelecteurLangue className="text-slate-700" />
            <span className="hidden text-sm text-slate-700 sm:inline">
              {abonne.prenom} {abonne.nom}
            </span>
            <Button
              variante="secondaire"
              taille="sm"
              onClick={() => {
                void seDeconnecter()
              }}
            >
              {' '}
              {texteInterface('Se déconnecter')}{' '}
            </Button>
          </div>
        </div>
        <nav className="mx-auto max-w-4xl px-4 pb-3">
          <Link className="underline" to="/portail/bons">
            {textesBons('Mes bons et crédits')}
          </Link>
          {' · '}<Link className="underline" to="/portail/transferts">{texteInterface.connu('Transférer un billet')}</Link>
        </nav>
      </header>

      <main className="mx-auto w-full max-w-4xl flex-1 px-4 py-8">
        <Suspense fallback={<ChargementPage libelle={texteInterface('Chargement de l’écran…')} />}>
          <Outlet />
        </Suspense>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto w-full max-w-4xl px-4 py-6 text-sm text-slate-500">
          <p>
            {!estEvenement(typeTenant) &&
              texteInterface(
                'Vous pouvez réserver les dates de vos formules au choix et carnets depuis cet espace.',
              )}
            {texteInterface(
              ' Pour corriger vos coordonnées, signaler un billet perdu ou demander un report, ouvrez ',
            )}
            <Link className="underline" to="/portail/demandes">
              {' '}
              {texteInterface('Mes demandes')}{' '}
            </Link>
            {texteInterface('. La billetterie examine et valide chaque dossier.')}
          </p>
        </div>
      </footer>
    </div>
  )
}
